/**
 * Aurora — a slow-drifting gradient-mesh backdrop for chrome surfaces
 * (page headers, the login panel). Three heavily-blurred brand-toned blobs
 * animate behind the content; it is purely decorative (aria-hidden,
 * pointer-events: none) and sits at z-index 0 so real content layers above it.
 *
 * Tokens only — re-themes in dark mode for free. The drift collapses to a
 * static mesh under prefers-reduced-motion via the global guard in globals.css.
 *
 * Wrap a header band:
 *   <div style={{ position: 'relative', overflow: 'hidden' }}>
 *     <Aurora />
 *     <div style={{ position: 'relative', zIndex: 1 }}>…header…</div>
 *   </div>
 */
import type { CSSProperties } from 'react'

let injected = false

/** Inject the keyframes + blob classes once (module-scoped, idempotent). */
function ensureAuroraStyles() {
  if (injected || typeof document === 'undefined') return
  injected = true
  const el = document.createElement('style')
  el.dataset.itAurora = ''
  el.textContent = `
.it-aurora {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  border-radius: inherit;
}
.it-aurora__blob {
  position: absolute;
  width: 46%;
  aspect-ratio: 1;
  border-radius: 50%;
  filter: blur(64px);
  opacity: 0.5;
  will-change: transform;
}
.it-aurora__blob--a {
  top: -34%; left: -6%;
  background: radial-gradient(circle at 50% 50%, var(--brand-500), transparent 70%);
  animation: it-aurora-a 22s var(--ease-in-out) infinite alternate;
}
.it-aurora__blob--b {
  top: -50%; right: -4%;
  background: radial-gradient(circle at 50% 50%, var(--accent-500), transparent 70%);
  animation: it-aurora-b 26s var(--ease-in-out) infinite alternate;
}
.it-aurora__blob--c {
  bottom: -64%; left: 32%;
  background: radial-gradient(circle at 50% 50%, var(--info-500, var(--viz-3)), transparent 70%);
  animation: it-aurora-c 30s var(--ease-in-out) infinite alternate;
}
[data-theme='dark'] .it-aurora__blob { opacity: 0.34; }
@keyframes it-aurora-a { from { transform: translate3d(0,0,0) scale(1); } to { transform: translate3d(8%,12%,0) scale(1.15); } }
@keyframes it-aurora-b { from { transform: translate3d(0,0,0) scale(1.1); } to { transform: translate3d(-10%,8%,0) scale(0.95); } }
@keyframes it-aurora-c { from { transform: translate3d(0,0,0) scale(0.95); } to { transform: translate3d(6%,-10%,0) scale(1.2); } }
`
  document.head.appendChild(el)
}

export function Aurora({ style }: { style?: CSSProperties }) {
  ensureAuroraStyles()
  return (
    <div className="it-aurora" aria-hidden style={style}>
      <span className="it-aurora__blob it-aurora__blob--a" />
      <span className="it-aurora__blob it-aurora__blob--b" />
      <span className="it-aurora__blob it-aurora__blob--c" />
    </div>
  )
}
